/**
 * PROBAR UNA REGLA SOBRE UN CLIENTE DE VERDAD (10-10). Se abre desde cada regla de la
 * Documentación, y nunca en producción (allí el servidor no ofrece la prueba y esto no sale).
 *
 * Se busca un cliente de los que les llega esa regla, se mira qué pasaría (un ensayo, que no
 * escribe nada) o se ejecuta sobre su ficha, y después se le devuelve a como estaba. La prueba
 * la hace y la cuenta el servidor (backend/core/probar_regla.py): aquí solo se pinta. Lo que
 * se ordena para pintarlo está en lib/probarRegla.js.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { AlertTriangle, Eye, FlaskConical, Play, Search, Undo2 } from 'lucide-react';
import { detallesEnLinea } from '../lib/cambiosDelReloj';
import {
    correosDelResultado, cuantosNoSePueden, estadoEnLaLista, etiquetaDelResultado, fraseDeColocacion, fraseDelFallo,
    lineasDelResultado, mensajeDeDevolucion, pruebaSinDevolver,
} from '../lib/probarRegla';
import { TextoDelCorreo } from './CorreosDelSimulador';

const NO_SE_PUDO_BUSCAR = 'No se pudo buscar el cliente. Vuelve a intentarlo.';
const NO_SE_PUDO_PROBAR = 'No se pudo hacer la prueba. Vuelve a intentarlo.';
const NO_SE_PUDO_DEVOLVER = 'No se pudo devolver al cliente a como estaba. Vuelve a intentarlo.';

const Etiqueta = ({ children }) => <p className="text-[11px] uppercase tracking-wider text-white/40 mb-1">{children}</p>;

/**
 * Devolver a un cliente a como estaba. Lo usan el panel de una regla y la lista de pendientes.
 *
 * El servidor no devuelve a ciegas: si desde la prueba pasaron cosas de verdad en su reloj (un
 * pago, un reporte), contesta cuáles y espera una confirmación (`porConfirmar`). Y si algún
 * campo lo cambió otro después de la prueba, no lo toca y lo dice (`nota`).
 */
export const useDevolver = (alDevolver) => {
    const { api } = useAuth();
    const [devolviendo, setDevolviendo] = useState(null);
    const [porConfirmar, setPorConfirmar] = useState(null);
    const [nota, setNota] = useState('');
    const devolver = useCallback((clientId, forzar = false) => {
        setDevolviendo(clientId);
        setNota('');
        return api.post('/admin/documentacion/probar/devolver', { client_id: clientId, forzar })
            .then((res) => {
                const hecho = res.data || {};
                if (hecho.hace_falta_confirmar) {
                    setPorConfirmar({ clientId, frase: hecho.frase, entradas: hecho.entradas || [] });
                    return hecho;
                }
                setPorConfirmar(null);
                // Forzada, o con campos que no se tocaron, no ha quedado «como antes»: se dice.
                const mensaje = mensajeDeDevolucion(hecho);
                if (mensaje.aviso) { toast.warning(mensaje.texto); setNota(mensaje.texto); } else toast.success(mensaje.texto);
                if (alDevolver) alDevolver(hecho, clientId);
                return hecho;
            })
            .catch((e) => {
                console.error('[probar regla] devolver', e);
                toast.error(fraseDelFallo(e, NO_SE_PUDO_DEVOLVER));
                return null;
            })
            .finally(() => setDevolviendo(null));
    }, [api, alDevolver]);
    const cancelar = useCallback(() => setPorConfirmar(null), []);
    return { devolver, devolviendo, porConfirmar, cancelar, nota };
};

/** Lo que el servidor pide confirmar antes de devolver, y lo que dejó sin tocar al hacerlo. */
export const AvisosDeDevolucion = ({ estado }) => {
    const { devolver, devolviendo, porConfirmar, cancelar, nota } = estado;
    return (
        <>
            {porConfirmar && (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-2 min-w-0" data-testid="probar-confirmar-devolucion">
                    <p className="text-sm text-amber-300 break-words">{porConfirmar.frase}</p>
                    {porConfirmar.entradas.length > 0 && (
                        <ul className="list-disc pl-5 space-y-0.5 text-sm text-white/75">
                            {porConfirmar.entradas.map((e) => <li key={e} className="break-words">{e}</li>)}
                        </ul>
                    )}
                    <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                        <Button onClick={() => devolver(porConfirmar.clientId, true)} disabled={!!devolviendo}
                            data-testid="probar-devolver-forzar" className="bg-[#7A2E12] hover:bg-[#8F3615] text-white h-auto min-h-9 whitespace-normal text-left">
                            <Undo2 className="w-4 h-4 mr-2 shrink-0" />{devolviendo ? 'Devolviendo…' : 'Devolverlo de todos modos'}
                        </Button>
                        <Button onClick={cancelar} disabled={!!devolviendo} className="bg-[#1F1F1F] hover:bg-[#2A2A2A] text-white">
                            Dejarlo como está
                        </Button>
                    </div>
                </div>
            )}
            {nota && <p className="text-sm text-amber-300/90 break-words" data-testid="probar-nota-devolucion">{nota}</p>}
        </>
    );
};

/** Los clientes que tienen una prueba sin devolver, para que no se quede ninguno olvidado. */
export const PruebasSinDevolver = ({ pendientes, onDevuelto }) => {
    const estado = useDevolver(onDevuelto);
    const { devolver, devolviendo } = estado;
    if (!pendientes?.length) return estado.nota ? <AvisosDeDevolucion estado={estado} /> : null;
    return (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 space-y-2 max-w-4xl" data-testid="probar-sin-devolver">
            <p className="text-[15px] font-semibold text-amber-300 break-words">
                {pendientes.length === 1 ? 'Hay un cliente con una prueba sin devolver.' : `Hay ${pendientes.length} clientes con una prueba sin devolver.`}
            </p>
            <ul className="space-y-2">
                {pendientes.map((p) => (
                    <li key={p.client_id} className="flex flex-wrap items-center justify-between gap-2 text-sm min-w-0">
                        <span className="min-w-0 break-words">
                            <span className="text-white font-medium">{p.nombre}</span>
                            <span className="text-white/50"> · {pruebaSinDevolver(p).replace('Tiene una prueba sin devolver: ', '') || 'una regla'}</span>
                        </span>
                        <Button onClick={() => devolver(p.client_id)} disabled={devolviendo === p.client_id}
                            data-testid={`probar-devolver-pendiente-${p.client_id}`}
                            className="h-8 bg-[#1F1F1F] hover:bg-[#2A2A2A] text-white text-xs px-3">
                            <Undo2 className="w-3.5 h-3.5 mr-1.5" />{devolviendo === p.client_id ? 'Devolviendo…' : 'Devolver a como estaba'}
                        </Button>
                    </li>
                ))}
            </ul>
            <AvisosDeDevolucion estado={estado} />
        </div>
    );
};

const Resultado = ({ resultado }) => {
    const { suya, aLaVez } = lineasDelResultado(resultado);
    const correos = correosDelResultado(resultado);
    const colocacion = fraseDeColocacion(resultado);
    const etiqueta = etiquetaDelResultado(resultado);
    return (
        <div className="rounded-lg border border-[#2A2A2A] bg-[#0A0A0A] p-3 sm:p-4 space-y-3 min-w-0" data-testid="probar-resultado">
            <div className="flex flex-wrap items-center gap-2">
                <span data-testid="probar-titular"
                    className={`text-[15px] font-semibold break-words ${resultado.salto ? 'text-emerald-300' : 'text-amber-300'}`}>
                    {resultado.titular}
                </span>
                <span data-testid="probar-etiqueta" className={`text-xs border rounded-full px-2.5 py-0.5 ${{
                    hecha: 'border-[#FF671F]/40 text-[#FF671F] bg-[#FF671F]/10',
                    no: 'border-amber-500/40 text-amber-300 bg-amber-500/10',
                    ensayo: 'border-[#333] text-white/70 bg-white/5' }[etiqueta.tono]}`}>
                    {etiqueta.texto}
                </span>
            </div>
            {resultado.repetida && (
                <p className="text-sm text-amber-300/90 break-words">
                    Esta regla ya se había probado en este cliente: este es el resultado de entonces, no se ha ejecutado otra vez.
                    Para probarla de nuevo, devuélvelo antes a como estaba.
                </p>
            )}
            {resultado.porque_no && <p className="text-sm text-white/80 break-words" data-testid="probar-porque-no">{resultado.porque_no}</p>}
            {resultado.nota && <p className="text-sm text-white/60 break-words">{resultado.nota}</p>}

            {/* Si no salta no se toca nada: no hay «después», solo cómo está. */}
            <div className={`grid grid-cols-1 gap-3 ${resultado.despues ? 'md:grid-cols-2' : ''}`}>
                <div className="min-w-0">
                    <Etiqueta>{resultado.despues ? 'Antes' : 'Cómo está el cliente'}</Etiqueta>
                    <p className="text-sm text-white/80 break-words" data-testid="probar-antes">{resultado.antes?.frase}</p>
                </div>
                {resultado.despues && (
                    <div className="min-w-0">
                        <Etiqueta>Después</Etiqueta>
                        <p className="text-sm text-white break-words" data-testid="probar-despues">{resultado.despues.frase}</p>
                    </div>
                )}
            </div>

            {resultado.salto && (
                <div className="min-w-0">
                    <Etiqueta>{resultado.escrito ? 'Qué hizo la regla' : 'Qué haría la regla'}, el {resultado.instante_en_claro}</Etiqueta>
                    {suya?.detalles?.length > 0 ? (
                        <ul className="list-disc pl-5 space-y-0.5 text-sm text-white/85" data-testid="probar-efectos">
                            {suya.detalles.map((d) => <li key={d} className="break-words">{d}</li>)}
                        </ul>
                    ) : (
                        <p className="text-sm text-white/70 break-words">
                            Esta regla deja al cliente como estaba: no cambia nada ni apunta nada en su historial.
                        </p>
                    )}
                </div>
            )}
            {correos.map((c) => <TextoDelCorreo key={c.correo.clave} correo={c.correo} />)}

            {aLaVez.length > 0 && (
                <div className="min-w-0">
                    <Etiqueta>Otras reglas que saltaron a la vez</Etiqueta>
                    <ul className="space-y-1 text-sm text-white/70">
                        {aLaVez.map((g) => (
                            <li key={g.regla} className="break-words">{g.titulo}
                                {g.detalles.length > 0 && <span className="text-white/45"> · {detallesEnLinea(g)}</span>}</li>
                        ))}
                    </ul>
                </div>
            )}

            {resultado.pasos?.length > 0 && (
                <details className="min-w-0" data-testid="probar-pasos">
                    <summary className="cursor-pointer select-none text-sm text-white/60 hover:text-white">Cómo se colocó al cliente</summary>
                    <ol className="list-decimal pl-5 space-y-0.5 text-sm text-white/60 mt-2">
                        {resultado.pasos.map((p) => <li key={p} className="break-words">{p}</li>)}
                    </ol>
                    {colocacion && <p className="text-xs text-white/45 mt-2 break-words">{colocacion}</p>}
                </details>
            )}

            {resultado.salto && resultado.queda && (
                <p className="text-xs text-white/50 break-words" data-testid="probar-queda">
                    {resultado.escrito ? resultado.queda : `Si la ejecutas, ${resultado.queda.charAt(0).toLowerCase()}${resultado.queda.slice(1)}`}
                </p>
            )}
            {resultado.escrito && resultado.cliente?.email && (
                <p className="text-sm text-white/80 break-words">
                    Para verlo en su app, entra con la cuenta de <span className="text-white font-medium">{resultado.cliente.email}</span>.
                </p>
            )}
        </div>
    );
};

const ProbarRegla = ({ regla, donde, onCambio }) => {
    const { api } = useAuth();
    const [buscar, setBuscar] = useState('');
    const [lista, setLista] = useState(null);
    const [falloAlBuscar, setFalloAlBuscar] = useState(false);
    const [elegido, setElegido] = useState(null);
    const [trabajando, setTrabajando] = useState(null);
    const [resultado, setResultado] = useState(null);
    const [fallo, setFallo] = useState('');

    const cargar = useCallback((q) => {
        setFalloAlBuscar(false);
        return api.get(`/admin/documentacion/probar/${regla.id}/clientes`, { params: { q } })
            .then((res) => setLista(res.data))
            .catch((e) => { console.error('[probar regla] buscar', e); setFalloAlBuscar(true); });
    }, [api, regla.id]);

    // Sin texto, los clientes activos de los planes de la regla; con dos letras o más, se busca.
    useEffect(() => {
        const q = buscar.trim();
        const espera = setTimeout(() => cargar(q.length >= 2 ? q : ''), q ? 300 : 0);
        return () => clearTimeout(espera);
    }, [buscar, cargar]);

    const alDevolver = useCallback(() => {
        setResultado(null);
        setFallo('');
        setElegido((e) => (e ? { ...e, en_prueba: null } : e));
        cargar(buscar.trim().length >= 2 ? buscar.trim() : '');
        if (onCambio) onCambio();
    }, [buscar, cargar, onCambio]);
    const devolucion = useDevolver(alDevolver);
    const { devolver, devolviendo } = devolucion;

    const probar = (ensayo) => {
        if (!elegido || trabajando || !estadoEnLaLista(elegido).elegible) return;
        setTrabajando(ensayo ? 'ensayo' : 'ejecutar');
        setFallo('');
        api.post(`/admin/documentacion/probar/${regla.id}`, { client_id: elegido.client_id, ensayo })
            .then((res) => {
                setResultado(res.data);
                setElegido((e) => (e ? { ...e, en_prueba: res.data.en_prueba || null } : e));
                if (res.data.escrito && onCambio) onCambio();
            })
            .catch((e) => {
                console.error('[probar regla]', e);
                setResultado(null);
                setFallo(fraseDelFallo(e, NO_SE_PUDO_PROBAR));
            })
            .finally(() => setTrabajando(null));
    };

    const elegir = (c) => { setElegido(c); setResultado(null); setFallo(''); };
    const clientes = lista?.clientes || [];
    const sinDevolver = pruebaSinDevolver(elegido?.en_prueba);
    // Elegido para devolverlo, pero sin poder probarle esta regla: mirar y ejecutar, apagados.
    const delElegido = elegido ? estadoEnLaLista(elegido) : null;
    const noSePuedeProbar = !!delElegido && !delElegido.elegible;

    return (
        <div className="mt-4 pt-4 border-t border-[#1E1E1E] space-y-4 min-w-0" data-testid={`probar-panel-${regla.numero}`}>
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-1" data-testid="probar-aviso">
                {(donde?.avisos || []).map((a, i) => (
                    <p key={a} className={`text-sm break-words flex items-start gap-2 ${i === 0 ? 'text-amber-300' : 'text-white/70'}`}>
                        {i === 0 && <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
                        <span className={i === 0 ? '' : 'pl-6'}>{a}</span>
                    </p>
                ))}
            </div>

            <div className="space-y-2 min-w-0">
                <Etiqueta>1. Elige un cliente de los que les llega esta regla</Etiqueta>
                <div className="relative w-full sm:max-w-[360px]">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                    <input value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Nombre o correo…"
                        data-testid="probar-buscar"
                        className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF671F]" />
                </div>
                {falloAlBuscar && <p className="text-sm text-amber-300/90">{NO_SE_PUDO_BUSCAR}</p>}
                {!falloAlBuscar && lista === null && <p className="text-sm text-white/40">Buscando clientes…</p>}
                {!falloAlBuscar && lista !== null && clientes.length === 0 && (
                    <p className="text-sm text-white/50 break-words" data-testid="probar-sin-clientes">
                        {lista.con_texto
                            ? (lista.otros > 0
                                ? 'Hay clientes con ese nombre, pero de planes a los que no les llega esta regla.'
                                : 'Ningún cliente coincide con lo que buscas.')
                            : 'Ningún cliente activo tiene un plan al que le llegue esta regla.'}
                    </p>
                )}
                {clientes.length > 0 && (
                    <ul className="max-h-[232px] overflow-y-auto rounded-lg border border-[#1E1E1E] divide-y divide-[#1A1A1A]" data-testid="probar-clientes">
                        {clientes.map((c) => {
                            const estado = estadoEnLaLista(c);
                            const marcado = elegido?.client_id === c.client_id;
                            return (
                                <li key={c.client_id}>
                                    <button type="button" disabled={!estado.pulsable} onClick={() => elegir(c)}
                                        data-testid={`probar-cliente-${c.client_id}`} data-elegible={estado.elegible ? 'si' : 'no'}
                                        className={`w-full text-left px-3 py-2 min-w-0 ${marcado ? 'bg-[#FF671F]/10' : ''} ${estado.pulsable ? 'hover:bg-white/[0.03]' : 'opacity-60 cursor-not-allowed'}`}>
                                        <span className="block text-sm text-white break-words">
                                            {c.nombre}<span className="text-white/45"> · {c.plan_nombre}</span>
                                        </span>
                                        <span className="block text-xs text-white/45 break-words" style={{ overflowWrap: 'anywhere' }}>{c.email}</span>
                                        {estado.texto && <span className="block text-xs text-amber-300/80 break-words">{estado.texto}</span>}
                                        {c.en_prueba && (
                                            <span className="block text-xs text-amber-300/80 break-words">
                                                {pruebaSinDevolver(c.en_prueba)}{estado.elegible ? '' : '. Elígelo para devolverlo.'}
                                            </span>
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
                {cuantosNoSePueden(lista?.no_se_puede) && (
                    <p className="text-xs text-amber-300/80 break-words" data-testid="probar-no-se-pueden">{cuantosNoSePueden(lista.no_se_puede)}</p>
                )}
            </div>

            <div className="space-y-2 min-w-0">
                <Etiqueta>2. Mira qué pasaría, o ejecútala</Etiqueta>
                {!elegido && <p className="text-sm text-white/40">Elige antes un cliente.</p>}
                {elegido && (
                    <p className="text-sm text-white/80 break-words" data-testid="probar-elegido">
                        Cliente elegido: <span className="text-white font-medium">{elegido.nombre}</span>
                        <span className="text-white/45"> · {elegido.plan_nombre}</span>
                    </p>
                )}
                {noSePuedeProbar && (
                    <p className="text-sm text-amber-300/90 break-words" data-testid="probar-no-se-puede-probar">
                        Ahora no se le puede probar esta regla. {delElegido.texto}
                        {sinDevolver ? ' Sí puedes devolverlo a como estaba, aquí abajo.' : ''}
                    </p>
                )}
                <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                    <Button onClick={() => probar(true)} disabled={!elegido || !!trabajando || noSePuedeProbar} data-testid="probar-ensayo"
                        className="bg-[#1F1F1F] hover:bg-[#2A2A2A] text-white">
                        <Eye className="w-4 h-4 mr-2" />{trabajando === 'ensayo' ? 'Mirando…' : 'Ver qué pasaría'}
                    </Button>
                    <Button onClick={() => probar(false)} disabled={!elegido || !!trabajando || noSePuedeProbar} data-testid="probar-ejecutar"
                        className="bg-[#FF671F] hover:bg-[#E55A1A] text-white">
                        <Play className="w-4 h-4 mr-2" />{trabajando === 'ejecutar' ? 'Ejecutando…' : 'Ejecutar en este cliente'}
                    </Button>
                </div>
                {fallo && <p className="text-sm text-amber-300/90 break-words" data-testid="probar-fallo">{fallo}</p>}
            </div>

            {resultado && <Resultado resultado={resultado} />}

            {elegido && sinDevolver && (
                <div className="space-y-2 min-w-0" data-testid="probar-devolver-caja">
                    <Etiqueta>3. Cuando termines, devuélvelo</Etiqueta>
                    <p className="text-sm text-white/60 break-words">
                        {sinDevolver}.
                    </p>
                    <Button onClick={() => devolver(elegido.client_id)} disabled={devolviendo === elegido.client_id}
                        data-testid="probar-devolver" className="bg-[#1F1F1F] hover:bg-[#2A2A2A] text-white w-full sm:w-auto">
                        <Undo2 className="w-4 h-4 mr-2" />
                        {devolviendo === elegido.client_id ? 'Devolviendo…' : 'Devolverlo a como estaba'}
                    </Button>
                </div>
            )}
            <AvisosDeDevolucion estado={devolucion} />
        </div>
    );
};

/** El botón de cada regla. Si la regla no se puede probar desde aquí, lo dice en vez del botón. */
export const BotonProbar = ({ regla, abierto, onAlternar }) => {
    if (!regla.prueba?.se_puede) {
        return (
            <span className="text-sm text-white/40 break-words min-w-0" data-testid={`probar-no-${regla.numero}`}>
                {regla.prueba?.porque || 'No se puede probar desde aquí.'}
            </span>
        );
    }
    return (
        <button type="button" onClick={onAlternar} data-testid={`probar-abrir-${regla.numero}`}
            className="inline-flex items-center gap-1 text-sm text-[#FF671F] hover:underline">
            <FlaskConical className="w-3.5 h-3.5" /> {abierto ? 'Cerrar la prueba' : 'Probar'}
        </button>
    );
};

export default ProbarRegla;
